import { TabContext, TabList } from "@mui/lab";
import {
  Box,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Tab,
  Typography,
} from "@mui/material";
import { useAtomValue } from "jotai";
import { isEmpty } from "lodash-es";
import { useCallback, useMemo, useRef, useState } from "react";
import { brandingAtom, brandingImageTypeAtom } from "../brand-kit/store";
import { ImageShapeSelect } from "./image-shape-select";
import { VisualStyleSelect } from "./visual-style-select";

import type { CustomTemplates } from "../../pages/create-content/use-custom-templates";
import type { BrandKitManager } from "../brand-kit/types";
import type { SxProps } from "@mui/material";
import type { FC } from "react";

export const CustomStyleSelect: FC<{
  manager: BrandKitManager;
  customTemplates: CustomTemplates;
}> = ({ manager, customTemplates }) => {
  const { branding_type: globalBrandKitStyle } = useAtomValue(brandingAtom);
  const imageType = useAtomValue(brandingImageTypeAtom);
  const styleTab = globalBrandKitStyle.type === "standard" ? "standard" : "custom";
  const [selectedTab, setSelectedTab] = useState<"custom" | "standard" | null>(styleTab);
  const [prevStyleType, setPrevStyleType] = useState(globalBrandKitStyle.type);
  if (globalBrandKitStyle.type !== prevStyleType) {
    setPrevStyleType(globalBrandKitStyle.type);
    setSelectedTab(styleTab);
  }

  const squares = useMemo(() => customTemplates?.["Square Comms"] ?? [], [customTemplates]);
  const banners = useMemo(() => customTemplates?.["Landscape Comms"] ?? [], [customTemplates]);

  const selectedTemplate = useMemo(() => {
    if (globalBrandKitStyle.type === "standard") {
      return {
        name: imageType === "banner" ? "Banner" : "Square",
        id: imageType,
      };
    }

    const templates = [...squares, ...banners];
    const template = templates.find(
      (_template) => _template.template.template_code === globalBrandKitStyle.value,
    );
    return {
      name: template?.name,
      id: template?.template.template_code,
    };
  }, [banners, globalBrandKitStyle.type, globalBrandKitStyle.value, imageType, squares]);

  const handleSelectStandard = useCallback(
    (type: "square" | "banner") => () => {
      const imageStyle =
        globalBrandKitStyle.type === "standard" ? globalBrandKitStyle.value : "bold";
      manager.style.setSelected({ type: "standard", value: imageStyle });
      manager.imageType.setSelected(type);
    },
    [globalBrandKitStyle.type, globalBrandKitStyle.value, manager.style, manager.imageType],
  );

  const handleSelectCustom = useCallback(
    (type: "square" | "banner", template_code: string | null) => () => {
      if (!template_code) return;
      manager.style.setSelected({ type: "custom", value: template_code });
      manager.imageType.setSelected(type);
    },
    [manager.imageType, manager.style],
  );

  const maybeHideSx = (tab: "custom" | "standard"): SxProps =>
    selectedTab === tab ? {} : { height: 0, overflow: "hidden", padding: 0, margin: 0 };

  const selectRef = useRef<HTMLDivElement>(null);

  return (
    <Stack direction="row" sx={{ gap: 2 }}>
      {isEmpty(squares) && isEmpty(banners) ? (
        <>
          <ImageShapeSelect onChange={manager.imageType.setSelected} imageType={imageType} />
          <VisualStyleSelect
            onChange={manager.style.setSelected}
            brandStyle={globalBrandKitStyle}
          />
        </>
      ) : (
        <>
          {selectedTemplate && (
            <TabContext value={selectedTab || "custom"}>
              <FormControl
                onKeyDown={(e) => {
                  if (e.code === "ArrowDown" || e.code === "ArrowUp") {
                    setTimeout(() => {
                      const focussed =
                        selectRef.current?.getElementsByClassName("Mui-focusVisible")[0];
                      if (focussed?.clientHeight === 0) {
                        setSelectedTab(selectedTab === "custom" ? "standard" : "custom");
                      }
                    }, 100);
                  }
                }}
              >
                <InputLabel htmlFor="custom-style-select">Image shape</InputLabel>
                <Select
                  id="custom-style-select"
                  label="Image shape"
                  inputProps={{ "aria-label": "Image shape" }}
                  value={selectedTemplate.id}
                  native={false}
                  sx={{ width: "300px" }}
                  renderValue={() => (
                    <Typography variant="body1" sx={{ color: "text.primary" }}>
                      {selectedTemplate?.name}
                    </Typography>
                  )}
                  MenuProps={{ ref: selectRef }}
                >
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      return false;
                    }}
                    sx={{ p: 1 }}
                  >
                    <TabList
                      sx={{ paddingLeft: 1, border: "none" }}
                      onChange={(_, val: "custom" | "standard") => setSelectedTab(val)}
                    >
                      <Tab label="Your designs" value="custom" />
                      <Tab label="Standard" value="standard" />
                    </TabList>
                  </Box>

                  {!isEmpty(squares) && (
                    <ListSubheader sx={maybeHideSx("custom")}>Squares</ListSubheader>
                  )}
                  {squares?.map(({ name, template: { template_code } }) => (
                    <MenuItem
                      key={template_code}
                      onClick={handleSelectCustom("square", template_code)}
                      value={template_code!}
                      selected={
                        globalBrandKitStyle.type === "custom" &&
                        globalBrandKitStyle.value === template_code
                      }
                      sx={maybeHideSx("custom")}
                    >
                      {name}
                    </MenuItem>
                  ))}
                  {!isEmpty(banners) && (
                    <ListSubheader sx={maybeHideSx("custom")}>Banners</ListSubheader>
                  )}

                  {banners?.map(({ name, template: { template_code } }) => (
                    <MenuItem
                      key={template_code}
                      value={template_code!}
                      onClick={handleSelectCustom("banner", template_code)}
                      selected={
                        globalBrandKitStyle.type === "custom" &&
                        globalBrandKitStyle.value === template_code!
                      }
                      sx={maybeHideSx("custom")}
                    >
                      {name}
                    </MenuItem>
                  ))}

                  <MenuItem
                    value="square"
                    onClick={handleSelectStandard("square")}
                    selected={globalBrandKitStyle.type === "standard" && imageType === "square"}
                    sx={maybeHideSx("standard")}
                  >
                    Square
                  </MenuItem>
                  <MenuItem
                    value="banner"
                    onClick={handleSelectStandard("banner")}
                    selected={globalBrandKitStyle.type === "standard" && imageType === "banner"}
                    sx={maybeHideSx("standard")}
                  >
                    Banner
                  </MenuItem>
                </Select>
              </FormControl>
            </TabContext>
          )}
          {globalBrandKitStyle.type === "standard" && (
            <VisualStyleSelect
              onChange={manager.style.setSelected}
              brandStyle={globalBrandKitStyle}
            />
          )}
        </>
      )}
    </Stack>
  );
};
